/**
 * Shared hub state for Pay Validated Debts (sframe-204 → debts-taxes step dt-3) + subsection routes.
 */

export const PAY_DEBTS_HUB_FRAME_ID = 'sframe-204';

export const PAY_DEBTS_HUB_KEYS = ['s1', 's2', 's3', 's206', 's5'] as const;
export type PayDebtsHubKey = (typeof PAY_DEBTS_HUB_KEYS)[number];

export interface PayDebtsChildSpec {
  hubKey: PayDebtsHubKey;
  frameId: string;
  itemIds: readonly string[];
}

/** Checklist completeness for each subsection; used to reconcile hub booleans */
export const PAY_DEBTS_CHILD_SPECS: PayDebtsChildSpec[] = [
  { hubKey: 's1', frameId: 'sframe-204-s1', itemIds: ['prep-1', 'prep-2', 'prep-3'] },
  { hubKey: 's2', frameId: 'sframe-204-s2', itemIds: ['hi-1', 'hi-2', 'hi-3'] },
  { hubKey: 's3', frameId: 'sframe-204-s3', itemIds: ['tg-1', 'tg-2', 'tg-3'] },
  { hubKey: 's206', frameId: 'sframe-206', itemIds: ['gc-1', 'gc-2', 'gc-3'] },
  { hubKey: 's5', frameId: 'sframe-204-s5', itemIds: ['doc-1', 'doc-2', 'doc-3'] },
];

export function defaultPayDebtsHubSections(): Record<PayDebtsHubKey, boolean> {
  return { s1: false, s2: false, s3: false, s206: false, s5: false };
}

export function mergePayDebtsHubSections(
  raw: Record<string, unknown> | undefined
): Record<PayDebtsHubKey, boolean> {
  const base = defaultPayDebtsHubSections();
  const hub = raw?.hubSections as Partial<Record<PayDebtsHubKey, boolean>> | undefined;
  if (!hub || typeof hub !== 'object') return base;
  for (const k of PAY_DEBTS_HUB_KEYS) {
    if (typeof hub[k] === 'boolean') base[k] = hub[k];
  }
  return base;
}

export function countPayDebtsHubDone(sections: Record<PayDebtsHubKey, boolean>): number {
  return PAY_DEBTS_HUB_KEYS.filter((k) => sections[k]).length;
}

function allChecksTrue(rawChecks: Record<string, boolean> | undefined, ids: readonly string[]): boolean {
  if (!ids.length) return false;
  return ids.every((id) => !!rawChecks?.[id]);
}

/** Derive hub section completion from saved checklist data on subsection frames */
export async function derivePayDebtsSectionsFromChildren(
  loadFrameData: (frameId: string) => Promise<{ data?: Record<string, unknown> } | null | undefined>
): Promise<Record<PayDebtsHubKey, boolean>> {
  const next = defaultPayDebtsHubSections();
  for (const spec of PAY_DEBTS_CHILD_SPECS) {
    const framed = await loadFrameData(spec.frameId);
    const rawChecks = framed?.data?.checks as Record<string, boolean> | undefined;
    next[spec.hubKey] = allChecksTrue(rawChecks, spec.itemIds);
  }
  return next;
}

export async function savePayDebtsHubSectionsAndSyncDt3(
  loadFrameData: (frameId: string) => Promise<{ data?: Record<string, unknown> } | null>,
  saveFrameData: (frameId: string, data: Record<string, unknown>) => Promise<boolean>,
  updateStepProgress: (
    stepId: string,
    phase: string,
    updates: { status: 'not-started' | 'in-progress' | 'complete' }
  ) => Promise<boolean>,
  nextSections: Record<PayDebtsHubKey, boolean>
): Promise<boolean> {
  const existing = await loadFrameData(PAY_DEBTS_HUB_FRAME_ID);
  const mergedData: Record<string, unknown> = { ...(existing?.data || {}) };
  mergedData.hubSections = nextSections;
  const ok = await saveFrameData(PAY_DEBTS_HUB_FRAME_ID, mergedData);
  const doneCount = countPayDebtsHubDone(nextSections);
  await updateStepProgress('dt-3', 'debts-taxes', {
    status:
      doneCount >= PAY_DEBTS_HUB_KEYS.length ? 'complete' : doneCount > 0 ? 'in-progress' : 'not-started',
  });
  return ok;
}

/** Reconcile hub from child checklists (mirrors Plan Payment hub pattern on sframe-202) */
export async function reconcilePayDebtsHubFromChildren(
  loadFrameData: (frameId: string) => Promise<{ data?: Record<string, unknown> } | null>,
  saveFrameData: (frameId: string, data: Record<string, unknown>) => Promise<boolean>,
  updateStepProgress: (
    stepId: string,
    phase: string,
    updates: { status: 'not-started' | 'in-progress' | 'complete' }
  ) => Promise<boolean>
): Promise<Record<PayDebtsHubKey, boolean>> {
  const derived = await derivePayDebtsSectionsFromChildren(loadFrameData);
  await savePayDebtsHubSectionsAndSyncDt3(loadFrameData, saveFrameData, updateStepProgress, derived);
  return derived;
}

/** Called when a subsection updates its checklist: merge one hub flag, persist hub, sync dt-3. */
export async function persistPayDebtsHubSectionAndSyncDt3(
  loadFrameData: (frameId: string) => Promise<{ data?: Record<string, unknown> } | null>,
  saveFrameData: (frameId: string, data: Record<string, unknown>) => Promise<boolean>,
  updateStepProgress: (
    stepId: string,
    phase: string,
    updates: { status: 'not-started' | 'in-progress' | 'complete' }
  ) => Promise<boolean>,
  key: PayDebtsHubKey,
  done: boolean
): Promise<boolean> {
  const existing = await loadFrameData(PAY_DEBTS_HUB_FRAME_ID);
  const mergedData: Record<string, unknown> = { ...(existing?.data || {}) };
  const next = mergePayDebtsHubSections(mergedData as Record<string, unknown>);
  next[key] = done;
  mergedData.hubSections = next;
  return savePayDebtsHubSectionsAndSyncDt3(loadFrameData, saveFrameData, updateStepProgress, next);
}
